import React, { useRef, useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Minus, Pause, Play, Settings, X, Trash2, Plus, Loader2, AlertCircle } from 'lucide-react';
import { Modal, ModalContent } from '../shared/Modal';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useFinancialData } from '../../contexts/FinancialDataContext';
import { formatCurrency } from '../../utils/formatters';
import { MarketIndex } from '../../types';

const TickerItem: React.FC<{ item: MarketIndex; darkMode: boolean }> = ({ item, darkMode }) => {
  const isUp = item.changePercent > 0;
  const isDown = item.changePercent < 0;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const color = isUp
    ? (darkMode ? 'text-emerald-400' : 'text-emerald-600')
    : isDown
      ? (darkMode ? 'text-red-400' : 'text-red-600')
      : (darkMode ? 'text-slate-400' : 'text-slate-500');

  return (
    <span className="inline-flex items-center gap-2 px-4 whitespace-nowrap">
      <span className={`text-xs font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
        {item.symbol}
      </span>
      <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
        {formatCurrency(item.price, false)}
      </span>
      <span className={`inline-flex items-center gap-0.5 text-[11px] font-medium ${color}`}>
        <Icon size={10} />
        {`${isUp ? '+' : ''}${item.changePercent.toFixed(2)}%`}
      </span>
      <span className={`text-[1px] ${darkMode ? 'text-slate-700' : 'text-slate-300'}`}>|</span>
    </span>
  );
};

export const MarketSnapshotWidget: React.FC = () => {
  const { darkMode } = useTheme();
  const { paymentTier, sheetId } = useAuth();
  const { marketSnapshot, handleAddMarketTicker, handleDeleteMarketTicker } = useFinancialData();
  const scrollRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef(0);
  const pausedRef = useRef(false);
  const hoveredRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [newSymbol, setNewSymbol] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  const isPro = paymentTier === 'pro';

  const touchStartRef = useRef<{ x: number; y: number; pos: number } | null>(null);
  const isDraggingRef = useRef(false);
  const velocityRef = useRef(0);
  const lastTouchRef = useRef<{ x: number; time: number } | null>(null);
  const momentumRef = useRef<number | null>(null);

  pausedRef.current = paused;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || marketSnapshot.length === 0) return;

    let animationId: number;

    const step = () => {
      if (!pausedRef.current && !hoveredRef.current && !isDraggingRef.current && momentumRef.current === null) {
        positionRef.current += 0.5;
        const halfWidth = el.scrollWidth / 2;
        if (positionRef.current >= halfWidth) positionRef.current = 0;
        el.style.transform = `translateX(-${positionRef.current}px)`;
      }
      animationId = requestAnimationFrame(step);
    };

    animationId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationId);
  }, [marketSnapshot.length]);

  const applyMomentum = () => {
    const el = scrollRef.current;
    if (!el) return;
    const halfWidth = el.scrollWidth / 2;

    const tick = () => {
      velocityRef.current *= 0.95; // friction
      if (Math.abs(velocityRef.current) < 0.3) {
        velocityRef.current = 0;
        momentumRef.current = null;
        return;
      }
      positionRef.current += velocityRef.current;
      if (positionRef.current < 0) positionRef.current += halfWidth;
      if (positionRef.current >= halfWidth) positionRef.current -= halfWidth;
      el.style.transform = `translateX(-${positionRef.current}px)`;
      momentumRef.current = requestAnimationFrame(tick);
    };
    momentumRef.current = requestAnimationFrame(tick);
  };

  const stopMomentum = () => {
    if (momentumRef.current !== null) {
      cancelAnimationFrame(momentumRef.current);
      momentumRef.current = null;
    }
    velocityRef.current = 0;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    stopMomentum();
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, pos: positionRef.current };
    lastTouchRef.current = { x: touch.clientX, time: Date.now() };
    isDraggingRef.current = false;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current || !scrollRef.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;

    // Only start horizontal drag if movement is mostly horizontal
    if (!isDraggingRef.current && Math.abs(dx) > 5 && Math.abs(dx) > Math.abs(dy)) {
      isDraggingRef.current = true;
    }

    if (isDraggingRef.current) {
      e.preventDefault();
      const halfWidth = scrollRef.current.scrollWidth / 2;
      let newPos = touchStartRef.current.pos - dx;
      if (newPos < 0) newPos += halfWidth;
      if (newPos >= halfWidth) newPos -= halfWidth;
      positionRef.current = newPos;
      scrollRef.current.style.transform = `translateX(-${positionRef.current}px)`;

      // Track velocity from recent movement
      const now = Date.now();
      if (lastTouchRef.current) {
        const dt = now - lastTouchRef.current.time;
        if (dt > 0) {
          velocityRef.current = -(touch.clientX - lastTouchRef.current.x) / dt * 16; // px per frame
        }
      }
      lastTouchRef.current = { x: touch.clientX, time: now };
    }
  };

  const handleTouchEnd = () => {
    if (!isDraggingRef.current) {
      // It was a tap, not a swipe — toggle pause
      setPaused(p => !p);
    } else if (Math.abs(velocityRef.current) > 1) {
      // Flick — apply momentum
      applyMomentum();
    }
    touchStartRef.current = null;
    lastTouchRef.current = null;
    isDraggingRef.current = false;
  };

  if (marketSnapshot.length === 0) return null;

  const activeTickers = marketSnapshot.filter(item => item.price > 0);
  if (activeTickers.length === 0) return null;

  const handleAdd = async () => {
    if (!newSymbol.trim()) return;
    if (!sheetId) {
      setAddError('No spreadsheet connected. Sign in and link your sheet, then try again.');
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      const price = await handleAddMarketTicker(
        newSymbol.trim().toUpperCase(),
        newLabel.trim() || newSymbol.trim().toUpperCase(),
        sheetId
      );
      if (price > 0) {
        setNewSymbol('');
        setNewLabel('');
      } else {
        setAddError(`"${newSymbol.trim().toUpperCase()}" not found in Google Finance database.`);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add ticker';
      setAddError(message);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (index: number) => {
    if (!sheetId) {
      setAddError('No spreadsheet connected. Sign in and link your sheet, then try again.');
      return;
    }
    setDeleting(index);
    setAddError(null);
    try {
      await handleDeleteMarketTicker(index, sheetId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove ticker';
      setAddError(message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      <div className={`rounded-xl overflow-hidden ${
        darkMode
          ? 'bg-white/5 border border-white/10'
          : 'bg-white/80 border border-slate-200 shadow-sm'
      }`}>
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-2">
            <TrendingUp size={14} className={darkMode ? 'text-slate-400' : 'text-slate-500'} />
            <h3 className={`text-xs font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
              Market Snapshot
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {isPro && (
              <button
                onClick={() => setShowSettings(true)}
                className={`p-1 rounded ${darkMode ? 'hover:bg-white/10 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}
              >
                <Settings size={12} />
              </button>
            )}
            <button
              onClick={() => setPaused(!paused)}
              className={`p-1 rounded ${darkMode ? 'hover:bg-white/10 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}
            >
              {paused ? <Play size={12} /> : <Pause size={12} />}
            </button>
          </div>
        </div>

        <div
          className="overflow-hidden py-2 cursor-default touch-pan-y"
          onMouseEnter={() => { hoveredRef.current = true; }}
          onMouseLeave={() => { hoveredRef.current = false; }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div ref={scrollRef} className="inline-flex will-change-transform">
            {activeTickers.map((item, i) => (
              <TickerItem key={`a-${i}`} item={item} darkMode={darkMode} />
            ))}
            {activeTickers.map((item, i) => (
              <TickerItem key={`b-${i}`} item={item} darkMode={darkMode} />
            ))}
          </div>
        </div>
      </div>

      {/* Settings Modal (Pro only) */}
      <Modal isOpen={showSettings} onClose={() => { setShowSettings(false); setAddError(null); }} maxWidth="lg">
        <ModalContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                Customize Tickers
              </h3>
              <button
                onClick={() => { setShowSettings(false); setAddError(null); }}
                className={`p-1 rounded-lg ${darkMode ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}
              >
                <X size={18} className={darkMode ? 'text-slate-500' : 'text-slate-400'} />
              </button>
            </div>

            {/* Add new ticker */}
            <div className="space-y-2">
              <p className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Add Ticker
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="flex gap-2 flex-1">
                  <input
                    type="text"
                    value={newSymbol}
                    onChange={(e) => { setNewSymbol(e.target.value); setAddError(null); }}
                    placeholder="Symbol (e.g. AAPL)"
                    onFocus={e => e.target.select()}
                    className={`w-32 sm:w-36 shrink-0 px-3 py-2 rounded-lg text-sm ${
                      darkMode
                        ? 'bg-white/10 border border-white/10 text-white placeholder-slate-500'
                        : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'
                    }`}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  />
                  <input
                    type="text"
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="Label (optional)"
                    onFocus={e => e.target.select()}
                    className={`flex-1 min-w-0 px-3 py-2 rounded-lg text-sm ${
                      darkMode
                        ? 'bg-white/10 border border-white/10 text-white placeholder-slate-500'
                        : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'
                    }`}
                    onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
                  />
                </div>
                <button
                  onClick={handleAdd}
                  disabled={adding || !newSymbol.trim()}
                  className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1 shrink-0 ${
                    adding || !newSymbol.trim()
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  } ${darkMode ? 'bg-violet-600 text-white hover:bg-violet-500' : 'bg-violet-600 text-white hover:bg-violet-700'}`}
                >
                  {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                  Add
                </button>
              </div>
              {addError && (
                <div className="flex items-center gap-1.5 text-red-500 text-xs">
                  <AlertCircle size={12} />
                  {addError}
                </div>
              )}
            </div>

            {/* Current tickers list */}
            <div className="space-y-1">
              <p className={`text-xs font-medium ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Current Tickers ({marketSnapshot.length})
              </p>
              <div className={`max-h-64 overflow-y-auto rounded-lg border ${
                darkMode ? 'border-white/10' : 'border-slate-200'
              }`}>
                {marketSnapshot.map((item, index) => {
                  const isUp = item.changePercent > 0;
                  const isDown = item.changePercent < 0;
                  const color = isUp
                    ? (darkMode ? 'text-emerald-400' : 'text-emerald-600')
                    : isDown
                      ? (darkMode ? 'text-red-400' : 'text-red-600')
                      : (darkMode ? 'text-slate-400' : 'text-slate-500');

                  return (
                    <div
                      key={index}
                      className={`flex items-center justify-between px-3 py-2 group ${
                        index > 0 ? (darkMode ? 'border-t border-white/5' : 'border-t border-slate-100') : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold w-12 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                          {item.symbol}
                        </span>
                        <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                          {item.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                          {item.price > 0 ? formatCurrency(item.price, false) : '—'}
                        </span>
                        <span className={`text-xs font-medium w-16 text-right ${color}`}>
                          {item.price > 0 ? `${isUp ? '+' : ''}${item.changePercent.toFixed(2)}%` : 'N/A'}
                        </span>
                        <button
                          onClick={() => handleDelete(index)}
                          disabled={deleting === index}
                          title="Remove ticker"
                          className={`p-1 rounded transition-opacity ${
                            darkMode ? 'hover:bg-white/10 text-slate-400 hover:text-red-400' : 'hover:bg-slate-100 text-slate-400 hover:text-red-500'
                          }`}
                        >
                          {deleting === index
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Trash2 size={12} />}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
